import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './supabaseClient';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import QuestionBank from './pages/QuestionBank';
import Exam from './pages/Exam';
import ExamStart from './pages/ExamStart';
import ExamResult from './pages/ExamResult';
import Profile from './pages/Profile';
import Mistakes from './pages/Mistakes';
import Messages from './pages/Messages';
import MessageDetail from './pages/MessageDetail';
import QuestionPractice from './pages/QuestionPractice';
import StudyReport from './pages/StudyReport';
import SubjectExam from './pages/SubjectExam';
import SubjectExamResult from './pages/SubjectExamResult';
import ExamSubjects from './pages/ExamSubjects';
import ExamAnswerDetail from './pages/ExamAnswerDetail';
import VIP from './pages/VIP';
import Achievements from './pages/Achievements';

/**
 * 路由保护组件，检查用户是否已登录
 * 如果已登录，显示目标组件；否则重定向到登录页面
 * @param {Object} props - 组件参数
 * @returns {JSX.Element} 路由保护组件
 */
const ProtectedRoute = ({ element, session }) => {
  // 检查本地存储中是否有用户信息
  const userId = localStorage.getItem('user_id');
  
  // 如果已登录（通过会话或本地存储）则渲染目标组件，否则重定向到登录页
  if (session || userId) {
    return element;
  } else {
    return <Navigate to="/login" />;
  }
};

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen app-bg-color">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<ProtectedRoute element={<Home session={session} />} session={session} />} />
        <Route path="/login" element={
          (!session && !localStorage.getItem('user_id')) 
            ? <Login /> 
            : <Navigate to="/" replace />
        } />
        <Route path="/register" element={
          (!session && !localStorage.getItem('user_id')) 
            ? <Register /> 
            : <Navigate to="/" replace />
        } />
        <Route path="/question-bank" element={<ProtectedRoute element={<QuestionBank session={session} />} session={session} />} />
        <Route path="/exam-subjects/:examId" element={<ProtectedRoute element={<ExamSubjects session={session} />} session={session} />} />
        <Route path="/exam" element={<ProtectedRoute element={<Exam session={session} />} session={session} />} />
        <Route path="/exam-start/:examId" element={<ProtectedRoute element={<ExamStart session={session} />} session={session} />} />
        <Route path="/exam-result/:examId" element={<ProtectedRoute element={<ExamResult session={session} />} session={session} />} />
        <Route path="/subject-exam/:examId/:subjectId" element={<ProtectedRoute element={<SubjectExam session={session} />} session={session} />} />
        <Route path="/subject-exam-result/:examId/:subjectId" element={<ProtectedRoute element={<SubjectExamResult session={session} />} session={session} />} />
        <Route path="/exam-answer-detail/:examId/:subjectId" element={<ProtectedRoute element={<ExamAnswerDetail />} session={session} />} />
        <Route path="/exam-answer-detail/:examId/:subjectId/:historyId" element={<ProtectedRoute element={<ExamAnswerDetail />} session={session} />} />
        <Route path="/profile" element={<ProtectedRoute element={<Profile session={session} />} session={session} />} />
        <Route path="/mistakes" element={<ProtectedRoute element={<Mistakes session={session} />} session={session} />} />
        <Route path="/messages" element={<ProtectedRoute element={<Messages session={session} />} session={session} />} />
        <Route path="/message-detail/:id" element={<ProtectedRoute element={<MessageDetail session={session} />} session={session} />} />
        <Route path="/question-practice/:subjectId" element={<ProtectedRoute element={<QuestionPractice session={session} />} session={session} />} />
        <Route path="/study-report" element={<ProtectedRoute element={<StudyReport session={session} />} session={session} />} />
        <Route path="/vip" element={<ProtectedRoute element={<VIP />} session={session} />} />
        <Route path="/achievements" element={<ProtectedRoute element={<Achievements session={session} />} session={session} />} />
      </Routes>
    </Router>
  );
}

export default App;
